/* eslint-disable global-require */
import React, { useContext, useEffect } from 'react'
import { Head } from '@notino/react-toolkit/exports/Head'
import { useIntl } from 'react-intl'

// types
import { ReservationResponse } from '../../../../types/types'

// utils
import { DEFAULT_LOCALE, getEnvironmentAndLocaleSpecificShopsConfig, getEnvironmentSpecificShopsConfig } from '../../../../appDefaults'
import { formatReservationDateRange, getDateTime, PAGE_LINKS } from '../../../../utils/helper'
import { AppContext } from '../../../../utils/appProvider'

// hooks
import useMessages from '../../../../hooks/useMessages'

type Props = {
	reservation: NonNullable<ReservationResponse['reservation']>
}

const MyReservationPageHead = (props: Props) => {
	const { reservation } = props
	const { locale } = useIntl()
	const { messages } = useMessages()
	const { assetsPath, productionEnvironment } = useContext(AppContext)
	const { formatDate, formatMessage } = useIntl()

	const defaultShop = getEnvironmentAndLocaleSpecificShopsConfig(productionEnvironment, DEFAULT_LOCALE)
	const currentShop = getEnvironmentAndLocaleSpecificShopsConfig(productionEnvironment, locale)
	const shops = getEnvironmentSpecificShopsConfig(productionEnvironment)
	const currentPath = `${currentShop.url}${PAGE_LINKS['/salons/my-reservations/:reservationID'](currentShop.locale, reservation.id)}`

	const dateTimeRange = formatReservationDateRange(
		formatDate,
		getDateTime(reservation.calendarEvent.start.date, reservation.calendarEvent.start.time).toDate(),
		getDateTime(reservation.calendarEvent.end.date, reservation.calendarEvent.end.time).toDate()
	)
	const reservationImage =
		reservation.calendarEvent.salon.images[0]?.resizedImages.small ||
		`${assetsPath}/${require('../../../../assets/images/basic-salon-placeholder-small.jpg')}`
	const pageTitle = formatMessage(
		{ id: 'SEO - My reservation page - meta title' },
		{
			serviceName: reservation.calendarEvent.service?.name ?? '',
			dateTimeRange
		}
	)

	const description = messages['SEO - My reservation page - meta description']
	const keywords = messages['SEO - My reservation page - meta keywords']

	const ogTitle = formatMessage(
		{ id: 'SEO - My reservation page - meta og title' },
		{
			serviceName: reservation.calendarEvent.service?.name ?? '',
			dateTimeRange
		}
	)
	const ogDescription = messages['SEO - My reservation page - meta og description']

	useEffect(() => {
		document.title = pageTitle
	}, [pageTitle])

	// NOTE: defining children as array and using .flat() on it otherwise <Head> doesn't work properly
	// SIDENOTE: each item has to have unique 'key' because <Head/> iterates over the array
	const children = [
		// charset
		<meta key='charset' charSet='utf-8' />,

		// title
		<title key='title'>{pageTitle}</title>,

		// description
		<meta key='description' name='description' content={description} />,

		// keywords
		<meta key='keywords' name='keywords' content={keywords} />,

		<meta key='robots' name='robots' content='noindex, nofollow' />,

		// canonical and alternate
		<link key='canonical' rel='canonical' href={currentPath} />,
		shops.map((shop, id) => (
			<link
				key={`alternate-${id}`}
				rel='alternate'
				href={`${shop.url}${PAGE_LINKS['/salons/my-reservations/:reservationID'](shop.locale, reservation.id)}`}
				hrefLang={shop.lang}
			/>
		)),
		<link
			key='x-default'
			rel='alternate'
			href={`${defaultShop.url}${PAGE_LINKS['/salons/my-reservations/:reservationID'](defaultShop.locale, reservation.id)}`}
			hrefLang='x-default'
		/>,

		// OG tags (social networks)
		<meta key='og:title' property='og:title' content={ogTitle} />,
		<meta key='og:description' property='og:description' content={ogDescription} />,
		<meta key='og:url' property='og:url' content={currentPath} />,
		<meta key='og:image' property='og:image' content={reservationImage} />
	]
		.flat()
		.filter(Boolean)

	return <Head>{children}</Head>
}

export default MyReservationPageHead
